import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { AlertTriangle } from "lucide-react";
import { Link } from "react-router-dom";

export default function LiveTradeWarningDialog({ open, onOpenChange, bot, onConfirm }) {
  const [agree1, setAgree1] = useState(false);
  const [agree2, setAgree2] = useState(false);
  const [agree3, setAgree3] = useState(false);
  const ready = agree1 && agree2 && agree3;

  useEffect(() => {
    if (!open) { setAgree1(false); setAgree2(false); setAgree3(false); }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0F0F0F] border-[#FF3B30] max-w-md" data-testid="live-warning-dialog">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <AlertTriangle size={22} className="text-[#FF3B30]" />
            <DialogTitle className="font-[Chivo] text-xl tracking-tight text-[#FF3B30]">
              Real Money — Final Confirmation
            </DialogTitle>
          </div>
        </DialogHeader>

        <div className="text-sm text-neutral-300 space-y-3">
          <p>
            You're about to start <span className="text-white font-[Chivo]">{bot?.name}</span> in <span className="text-[#FF3B30] font-[Chivo]">LIVE</span> mode.
            Real orders will be sent to <span className="text-white">{bot?.exchange?.toUpperCase()}</span> using your saved API keys.
          </p>
          <div className="border border-border bg-[#0A0A0A] p-3 text-xs space-y-1">
            <Row k="Symbol" v={bot?.symbol} />
            <Row k="Strategy" v={bot?.strategy} />
            <Row k="Trade size" v={`$${bot?.config?.trade_size_usd ?? 0}`} />
            <Row k="Frequency" v={`${bot?.config?.frequency_per_min ?? 0} / min`} />
            <Row k="Max trades" v={bot?.config?.max_trades ?? 0} />
            <Row k="Notional cap" v={`$${(bot?.config?.trade_size_usd ?? 0) * (bot?.config?.max_trades ?? 0)}`} />
          </div>
          <ul className="text-xs text-neutral-400 list-disc pl-5 space-y-0.5">
            <li>Micro-trade bots may incur many small fees that compound.</li>
            <li>Past simulated performance does NOT guarantee live results.</li>
            <li>You can stop the bot at any time from the Bots page.</li>
          </ul>

          <div className="border-t border-border pt-3 space-y-2">
            <label className="flex items-start gap-2 cursor-pointer">
              <Checkbox
                data-testid="live-confirm-1"
                checked={agree1}
                onCheckedChange={(v) => setAgree1(!!v)}
                className="mt-0.5 border-neutral-600 data-[state=checked]:bg-[#FF3B30] data-[state=checked]:border-[#FF3B30]"
              />
              <span className="text-xs text-neutral-300">
                I have read and accept the{" "}
                <Link to="/legal/risk" target="_blank" className="text-[#007AFF] hover:underline">Risk Disclosure</Link>{" "}
                and understand I can lose 100% of my capital.
              </span>
            </label>
            <label className="flex items-start gap-2 cursor-pointer">
              <Checkbox
                data-testid="live-confirm-2"
                checked={agree2}
                onCheckedChange={(v) => setAgree2(!!v)}
                className="mt-0.5 border-neutral-600 data-[state=checked]:bg-[#FF3B30] data-[state=checked]:border-[#FF3B30]"
              />
              <span className="text-xs text-neutral-300">
                I confirm I am not relying on this platform as investment advice. All trading decisions are my own.
              </span>
            </label>
            <label className="flex items-start gap-2 cursor-pointer">
              <Checkbox
                data-testid="live-confirm-3"
                checked={agree3}
                onCheckedChange={(v) => setAgree3(!!v)}
                className="mt-0.5 border-neutral-600 data-[state=checked]:bg-[#FF3B30] data-[state=checked]:border-[#FF3B30]"
              />
              <span className="text-xs text-neutral-300">
                I can afford to lose the full notional cap of this bot.
              </span>
            </label>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            data-testid="live-warning-cancel"
            variant="outline" className="border-border bg-transparent hover:bg-[#141414]"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            data-testid="live-warning-confirm"
            disabled={!ready}
            className="bg-[#FF3B30] hover:bg-[#ff5247] text-white disabled:opacity-40 disabled:cursor-not-allowed"
            onClick={onConfirm}
          >
            I understand — start live bot
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between">
      <span className="text-neutral-500">{k}</span>
      <span className="num text-white">{v}</span>
    </div>
  );
}
